import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import range from 'lodash/range';
import TextInput from '../../../../core/form/TextInput';
import Select from '../../../../core/form/Select';
import Row from '../../../../core/layout/Row';
import Column from '../../../../core/layout/Column';
import FormButtons from '../../../../core/form/FormButtons';
import FetchSelect from '../../../../core/form/FetchSelect';
import Button from '../../../../core/form/Button';
import Dialog from '../../../../core/layout/Dialog';
import Card from '../../../../core/layout/Card';

const UnitForm = (props) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Row>
        <Column size={3}>
          <TextInput
            required
            disabled={props.submitting || props.disabled}
            label="Unit Name"
            value={get(props.values, 'name', '')}
            onChange={(value) => props.onChange('name', value)}
            errorText={get(props.errors, 'name', '')}
          />
        </Column>
        <Column size={3}>
          <FetchSelect
            required
            showSearch
            url="modules"
            params={{
              verbose: false,
              query: {
                course: props.courseId,
              },
            }}
            disabled
            label="Module"
            value={get(props.values, 'module', '')}
            onChange={(module) => props.onChange('module', module)}
            errorText={get(props.errors, 'module', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        <Column size={3}>
          <FetchSelect
            required
            showSearch
            url={props.values.academicPlan ? `unit-types?query[academicPlan]=${props.values.academicPlan}` : null}
            disabled={props.submitting || props.disabled}
            label="Unit Type"
            value={get(props.values, 'type', '')}
            onChange={(type) => props.onChange('type', type)}
            errorText={get(props.errors, 'type', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
            resultFilter={(type) => !['demo', 'essay', 'movie', 'content video'].includes(type.name.toLowerCase())}
          />
        </Column>
        <Column size={3}>
          <FetchSelect
            showSearch
            url="grammars"
            fullWidth
            disabled={props.fetching}
            label="Grammars"
            value={get(props.values, 'grammars', '')}
            onChange={(grammar) => props.onChange('grammars', grammar)}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
      </Row>
      <Row>
        <Column size={3}>
          <Select
            required
            options={['A', 'B', 'C', 'D'].map((value) => ({
              value,
              label: value,
            }))}
            disabled={props.submitting || props.disabled}
            label="Group"
            value={get(props.values, 'group', '')}
            onChange={(value) => props.onChange('group', value)}
            errorText={get(props.errors, 'group', '')}
          />
        </Column>
        <Column size={3}>
          <Select
            required
            options={range(1, 41).map((value) => ({
              value,
              label: value.toString(),
            }))}
            disabled={props.submitting || props.disabled}
            label="Order"
            value={get(props.values, 'order', '')}
            onChange={(value) => props.onChange('order', value)}
            errorText={get(props.errors, 'order', '')}
          />
        </Column>
        <Column size={3}>
          <Select
            required
            options={range(70, 105, 5).map((value) => ({
              value,
              label: value.toString(),
            }))}
            disabled={true}
            label="Score to pass"
            value={get(props.values, 'scoreToPass', '')}
            onChange={(value) => props.onChange('scoreToPass', value)}
            errorText={get(props.errors, 'scoreToPass', '')}
          />
        </Column>
        <Column size={3}>
          <Select
            required
            options={[
              { label: 'EASY', value: 'EASY' },
              { label: 'MODERATE', value: 'MODERATE' },
              { label: 'HARD', value: 'HARD' },
            ]}
            disabled={props.submitting || props.disabled}
            label="Difficulty"
            value={get(props.values, 'difficulty', '')}
            onChange={(value) => props.onChange('difficulty', value)}
            errorText={get(props.errors, 'difficulty', '')}
          />
        </Column>
      </Row>
      {!props.values.id && (
        <Card
          title="Items"
          bordered={false}
          actions={<Button label="Update Items" onClick={() => setShowModal(true)} />}
        >
          <Row>
            <Column size={12}>
              {get(props.values, 'items', []).map((item, index) => (
                <Row key={item.text}>
                  {!!index && (
                    <div style={{ width: '100%', height: 1, backgroundColor: 'rgba(0, 0, 0, 0.1)', marginTop: -5 }} />
                  )}
                  <Column
                    size={
                      [
                        'VOCABULARY_ACADEMIC',
                        'MEANINGS_ASSOCIATING',
                        'VOCABULARY',
                        'PHONEME',
                        'SINGLE_CHOICE_KIDS',
                        'CONNECTING_DOTS',
                        'VOCABULARY_GAME',
                        'MEMORY_GAME',
                      ].includes(item.typeKey)
                        ? 2
                        : 6
                    }
                  >
                    <TextInput disabled label="Item text" value={item.text} />
                  </Column>
                  {[
                    'VOCABULARY_ACADEMIC',
                    'MEANINGS_ASSOCIATING',
                    'VOCABULARY',
                    'PHONEME',
                    'SINGLE_CHOICE_KIDS',
                    'CONNECTING_DOTS',
                    'VOCABULARY_GAME',
                    'MEMORY_GAME',
                  ].includes(item.typeKey) && (
                    <Column size={4}>
                      <TextInput
                        label="Item Post Phrase"
                        value={item.postPhrase}
                        onChange={(value) => {
                          props.onChange(
                            'items',
                            props.values.items.map((i) => {
                              if (i.text === item.text) {
                                return {
                                  ...i,
                                  postPhrase: value,
                                };
                              }
                              return i;
                            })
                          );
                        }}
                      />
                    </Column>
                  )}
                  <Column size={3}>
                    <FetchSelect
                      showSearch
                      allowClear={false}
                      url={`unit-types/${get(props.values, 'type', '')}/item-types`}
                      disabled={props.submitting || props.disabled}
                      label="Item Type"
                      value={item.type}
                      onChange={(value, type) => {
                        props.onChange(
                          'items',
                          props.values.items.map((i) => {
                            if (i.text === item.text) {
                              return {
                                ...i,
                                type: value,
                                time: type.defaultTime || 0,
                                typeKey: type.key,
                              };
                            }
                            return i;
                          })
                        );
                      }}
                      resultTransformer={{
                        text: 'name',
                        value: 'id',
                      }}
                    />
                  </Column>
                  <Column size={3}>
                    <FetchSelect
                      showSearch
                      url="characters"
                      apiVersion="v3"
                      isPaginated
                      disabled={
                        props.submitting ||
                        props.disabled ||
                        !item.type ||
                        ['VIDEO', 'VIDEO_SHORT', 'VIDEO_TEXT_AREA', 'TEXT', 'IMAGE'].includes(item.typeKey)
                      }
                      label="Character"
                      value={get(item, 'character', '')}
                      onChange={(character) => {
                        props.onChange(
                          'items',
                          props.values.items.map((i) => {
                            if (i.text === item.text) {
                              return {
                                ...i,
                                character,
                              };
                            }
                            return i;
                          })
                        );
                      }}
                      resultTransformer={{
                        text: 'name',
                        value: 'id',
                      }}
                    />
                  </Column>
                </Row>
              ))}
            </Column>
          </Row>
        </Card>
      )}
      <Dialog
        title="Update unit text"
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        actions={[
          <Button
            key="update-text"
            label="Update"
            type="primary"
            onClick={() => {
              props.onChange(
                'items',
                get(props.values, 'tempText', '')
                  .split('\n')
                  .map((text) => ({ text, type: undefined, grammar: get(props.value, 'grammars[0]', undefined) }))
              );
              props.onChange('tempText', '');
              setShowModal(false);
            }}
          />,
        ]}
      >
        <TextInput
          label=""
          fieldType="textarea"
          textAreaRows="10"
          value={get(props.values, 'tempText', '')}
          onChange={(value) => props.onChange('tempText', value)}
        />
      </Dialog>
      {!props.disabled && (
        <FormButtons
          confirmLabel={props.values.id ? 'Update Unit' : 'Create Unit'}
          isDisabled={props.submitting || !props.isDirty()}
          isSubmitting={props.submitting}
          onReset={props.onReset}
        />
      )}
    </form>
  );
};

UnitForm.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  isDirty: PropTypes.func,
  disabled: PropTypes.bool,
};

UnitForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onReset: () => false,
  disabled: false,
};

export default UnitForm;
